<template>
  <div class="fill-height">
    <template v-if="team && user && presentation && session">

      <v-dialog v-if="presentation.disclaimer"
                v-model="showDisclaimerDialog"
                max-width="800px"
                persistent>
        <DisclaimerCard :disclaimer="presentation.disclaimer"
                        @close="close"
                        @open="acknowledge">
        </DisclaimerCard>
      </v-dialog>

      <v-dialog v-model="showInternalUseOnlyDialog"
                max-width="800px"
                persistent>
        <v-card>
          <v-card-title class="text-uppercase">
            <v-icon left>
              fal fa-fw fa-exclamation-triangle
            </v-icon>
            For Internal Use Only
          </v-card-title>
          <v-card-text>
            This file is for internal use only. It is not to be shown to or shared with customers.
          </v-card-text>
          <v-card-actions>
            <v-btn @click.prevent="close">
              Cancel, close this file
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary"
                   @click="acknowledge">
              I acknowledge, proceed
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <view-video-presentation v-if="isVideo"
                               :presentation="presentation"
                               :session="session"
                               style="width: 100%;">
      </view-video-presentation>
      <view-image-presentation v-else-if="isImage"
                               :presentation="presentation"
                               :session="session"
                               style="width: 100%;">
      </view-image-presentation>
      <view-excel-presentation v-else-if="isExcel && useOnlyOffice"
                               :presentation="presentation"
                               :session="session"
                               style="width: 100%;">
      </view-excel-presentation>
      <view-powerpoint-presentation v-else-if="isPowerPoint && useOnlyOffice"
                                    :presentation="presentation"
                                    :session="session"
                                    style="width: 100%;">
      </view-powerpoint-presentation>
      <view-cad-presentation v-else-if="1===2 && isCAD"
                             :presentation="presentation"
                             :session="session"
                             style="width: 100%;">
      </view-cad-presentation>
      <view-html-presentation v-else-if="isHTML"
                              :presentation="presentation"
                              :session="session"
                              style="width: 100%;">
      </view-html-presentation>
      <ViewPdfPresentation v-else-if="isPDF"
                           :presentation="presentation"
                           :session="session"
                           :slides="slides"
                           :slide-notes="slideNotes"
                           style="width: 100%;"
                           @change_page="changePage"
                           @show-notes="openNotesWindow">
      </ViewPdfPresentation>
      <v-alert v-else
               type="error">
        {{ presentation.name }} cannot be viewed in the browser because its type is {{ presentation.type }}.
      </v-alert>
    </template>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import moment from 'moment-timezone';
import {isInteger, isNull} from 'lodash';
import ViewExcelPresentation from './components/ViewExcelPresentation';
import ViewPdfPresentation from './components/ViewPDFPresentation';
import ViewPowerpointPresentation from './components/ViewPowerpointPresentation';
import ViewVideoPresentation from './components/ViewVideoPresentation';
import ViewImagePresentation from "./components/ViewImagePresentation";
import ViewCadPresentation from "./components/ViewCADPresentation";
import ViewHtmlPresentation from "./components/ViewHTMLPresentation";
import DOMPurify from 'dompurify';
import deviceSessionService from '../../../../ml/services/deviceSessionService';
import contentService from '../../services/contentService';
import visibilityService from '../../services/visibilityService';
import DisclaimerCard from '../disclaimers/components/DisclaimerCard.vue';
import slideDeckService from '../../services/slideDeckService';

let sessionInterval = null;


/**
 * MLD-2359
 */
export default {
  components: {DisclaimerCard, ViewHtmlPresentation, ViewCadPresentation, ViewExcelPresentation, ViewImagePresentation, ViewPdfPresentation, ViewPowerpointPresentation, ViewVideoPresentation,},
  metaInfo() {
    return {
      title: this.presentation ? this.presentation.name : 'Mobile Locker',
    }
  },
  data() {
    return {
      acknowledged: false, // MLD-2864
      showInternalUseOnlyDialog: false, // MLD-2864
      showDisclaimerDialog: false, // MLD-5970
      busy: false,
      error: null,
      // MLD-4832
      intervalDelay: 5000, // milliseconds
      pingFailures: 0,
      maxPingFailures: 5,
      slides: [], // MLD-6435
      slide: null, // MLD-6435
      slideNotes: [], // MLD-6435
      notesWindow: null, // MLD-6435
      pageNumber: null, // MLD-6435
    }
  },
  computed: {
    ...mapGetters([
      'crmLabel', // MLD-2940
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'activity',
      'activityID',
      'liveMode', // MLD-2940
      'mustExitProperly', // MLD-2940
      'presentation',
      'presentations',
      'selectedCustomers', // MLD-5823
      'session',
    ]),
    ...mapGetters('meta', [
      'meta',
    ]),
    slideIDs() { // MLD-6435
      return this.slides.map(s => s.id);
    },
    activityID() { // MLD-2463
      if (this.$route.query.activity_id) {
        return parseInt(this.$route.query.activity_id);
      }
      return null;
    },
    sessionUUID() {
      return this.$route.query.session_uuid;
    },
    sessionStartedAt() {
      return this.$route.query.session_started_at;
    },
    isHTML() { // MLD-3069 is it HTML and is it using the CDN
      return this.presentation
        && this.presentation.type_html
        && this.presentation.settings
        && this.presentation.settings.can_user_use_cdn
        && this.team
        && this.team.is_content_domain_enabled;
    },
    isExcel() {
      return this.presentation
        && this.presentation.type_excel;
    },
    isCAD() { // MLD-3043
      return this.presentation
        && this.presentation.type_cad;
    },
    isImage() { // MLD-3000
      return this.presentation
        && this.presentation.type_image;
    },
    isPDF() {
      return this.presentation
        && (this.presentation.type_pdf || this.presentation.converts_to_pdf)
    },
    isPowerPoint() {
      return this.presentation
        && this.presentation.type_powerpoint;
    },
    useOnlyOffice() { // MLD-2908
      return this.team.onlyoffice_enabled
        && this.presentation
        && (this.isExcel || this.isPowerPoint)
        && this.session
        && this.session.data
        && this.session.data.input
        && this.session.data.input.use_onlyoffice;
    },
    isVideo() {
      return this.presentation
        && this.presentation.type_video;
    },
    canBeEmailed() {
      return this.presentation
        && !!this.presentation.settings.can_be_shared;
    },
    canBeDownloaded() {
      return this.presentation
        && !!this.presentation.settings.can_be_exported;
    },
    presentationID() {
      return parseInt(this.$route.params.presentationID);
    },
    isReturnToCRM() {
      try {
        return parseInt(this.$route.query.return_to_crm) === 1;
      } catch (e) {
        return false;
      }
    },
    returnPrompt() { // MLD-2940
      return `You must click RETURN TO ${this.crmLabel}.`
    },
  },
  beforeRouteUpdate(to, from, next) {
    //console.log('beforeRouteUpdate()');
    if (to.params.presentationID) {
      this.fetchData(to.params.presentationID)
    }
    next();
  },
  async beforeRouteLeave(to, from, next) { // MLD-2940
    //console.log('beforeRouteLeave()');
    await this.endSession();
    next();
  },
  async beforeDestroy() {
    //console.log('beforeDestroy()');
    this.closeNotesWindow(); // MLD-6435
    this.deregisterPingFunction();
    await this.endSession();
    this.removeVisibilityChecking();
    window.removeEventListener('beforeunload', this.promptBeforeUnloading, {capture: true}); // MLD-2940
  },
  created() {
    this.$store.dispatch('meta/initialize');
    //console.log('created()');
    this.initVisibilityChecking(); // MLD-4832
    window.addEventListener('beforeunload', this.promptBeforeUnloading, {capture: true}); // MLD-2940
    this.fetchData(this.presentationID);
  },
  methods: {
    // MLD-4832
    hasFocus() {
      return visibilityService.hasFocus();
    },
    getVisibilityState() {
      return visibilityService.getVisibilityState();
    },
    isTabVisible() {
      return visibilityService.isTabVisible();
    },
    initVisibilityChecking() { // MLD-4832
      document.addEventListener('visibilitychange', this.visibilityChangeListener);
    },
    removeVisibilityChecking() { // MLD-6890
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    },
    visibilityChangeListener() {
      if (this.session) {
        if (this.isTabVisible()) {
          this.registerPingFunction();
        } else {
          this.deregisterPingFunction();
        }
        this.updateMetadata(this.meta.elapsedMS);
        const form = {
          session_id: this.session.uuid,
          session_started_at: moment(this.session.started_at).valueOf(),
          presentation_id: this.session.presentation_id,
          elapsed_ms: this.meta.elapsedMS,
          category: 'visibility',
          action: 'change',
          uri: this.getVisibilityState(),
        }
        console.log(`Visibility State: ${this.getVisibilityState()} - ${this.meta.elapsedMS} ms`);
        contentService.logEvent(form)
          .catch(() => {
          });
      }
    },
    updateMetadata(elapsed = null) {
      this.$store.dispatch('meta/updateMetadata', elapsed);
    },
    changePage(pageNumber) {
      //console.log('changePage', pageNumber);
      this.updateMetadata();
      this.pageNumber = pageNumber;
      if (pageNumber > 0 && this.slides.length >= pageNumber) {
        this.slide = this.slides[pageNumber - 1];
      } else {
        this.slide = null;
      }
      contentService.changePageMeta(this.session, this.presentation, this.meta, pageNumber, this.slide)
        .then(() => {
          this.$store.commit('meta/setCurrentPage', pageNumber);
          this.updateNotesWindow(pageNumber);
        })
        .catch(() => {
        })
    },
    async promptBeforeUnloading(event) { // MLD-2940
      //console.log('promptBeforeUnloading')
      if (this.isReturnToCRM && this.liveMode && this.mustExitProperly) {
        event.preventDefault();
        return event.returnValue = this.returnPrompt;
      }
      await this.endSession();
    },
    async fetchData(presentationID) {
      this.acknowledged = false;
      this.showInternalUseOnlyDialog = false;
      this.showDisclaimerDialog = false; // MLD-5970
      this.error = null;
      this.busy = true;
      try {
        await this.$store.dispatch('content/fetchPresentation', presentationID); // MLD-3342

        this.slides = (await slideDeckService.getPresentationSlidesFor(presentationID)).map(ps => ps.slide);
        this.slideNotes = (await slideDeckService.getNotesForCurrentUser()).filter(n => this.slideIDs.includes(n.slide_id));

        //await this.$store.dispatch('content/activatePresentation', presentationID);
        this.showDisclaimerDialog = this.presentation.disclaimer?.is_active || false; // MLD-5970
        this.acknowledged = !this.showDisclaimerDialog;
        if (this.showDisclaimerDialog === false) {
          this.acknowledged = !this.presentation.is_internal_use_only; // MLD-2864
          this.showInternalUseOnlyDialog = this.presentation.is_internal_use_only;
        }
        await this.$store.dispatch('content/fetchSession', this.sessionUUID);
        await this.$store.dispatch('content/setMustExitProperly', true); // MLD-2940
        if (this.presentation && this.presentation.is_internal_use_only) { // MLD-3086
          await this.$store.dispatch('content/setLiveMode', false);
        }
        if (this.presentation && this.session && this.session.uuid) { // MLD-4832
          //this.meta.presentationID = this.presentation.id;
          this.$store.commit('meta/setPresentationID', this.presentation.id);
          this.registerPingFunction();
        }
      } catch (err) {
        this.$toast.error('You cannot view that presentation.'); // MLD-5483
        /*if ("Bugsnag" in window) {
          const options = {
            cause: err,
          }
          const error = new Error('Failed to fetchData in AppPresentPresentation.vue', options);
          bugsnagService.notify(err); // MLD-5144
        }*/
        await this.$router.replace({name: `${this.$root.routePrefix}.home`});
      } finally {
        this.busy = false;
      }
    },
    deregisterPingFunction() {
      //console.log('deregisterPingFunction()');
      clearInterval(sessionInterval);
      sessionInterval = null;
    },
    registerPingFunction() {
      if (isNull(sessionInterval)) {
        //console.log('registerPingFunction()');
        sessionInterval = setInterval(() => {
          this.pingSession();
        }, this.intervalDelay);
      }
    },
    isPingable() {
      return this.presentation
        && this.session
        && this.sessionUUID
        && this.isTabVisible()
        && isInteger(sessionInterval);
    },
    pingSession() {
      if (this.isPingable()) {
        const diff = Date.now() - this.meta.timestamp;
        const elapsedMS = this.meta.elapsedMS + diff;
        return deviceSessionService.pingSession(this.sessionUUID, elapsedMS)
          .then(() => {
            this.updateMetadata(elapsedMS);
          })
          .catch(() => {
            this.pingFailures = this.pingFailures + 1;
            if (this.pingFailures > this.maxPingFailures) {
              this.deregisterPingFunction();
              console.log('Ping failures reached, stopping');
            }
          })
      } else {

      }
    },
    acknowledge() { // MLD-2864
      this.acknowledged = true;
      this.showInternalUseOnlyDialog = false;
      this.showDisclaimerDialog = false; // MLD-5970
    },
    close() { // MLD-2864
      this.acknowledged = false;
      if (this.isReturnToCRM) {
        this.returnToCRM();
      } else {
        this.returnToContentLibrary();
      }
    },
    async returnToCRM() { // MLD-2463
      const crmName = this.crmLabel;
      if (!confirm(`Are you sure you want to end this session and return to ${DOMPurify.sanitize(crmName)}?`)) {
        return;
      }
      if (this.session) {
        await this.endSession().catch(() => {
        });
      }
      window.close();
    },
    async returnToContentLibrary() { // MLD-2811
      await this.$router.push({name: `${this.$root.routePrefix}.home`});
    },
    async endSession() { // MLD-4832
      if (this.session) { // Close the session before leaving the route
        this.updateMetadata();

        const payload = {
          session: this.session,
          meta: this.meta,
        }
        const sessionEntities = this.selectedCustomers.map(customer => { // MLD-5823
          return {
            object: customer.object,
            object_id: customer.id,
          }
        });
        if (sessionEntities.length > 0) { // MLD-5823
          payload.session_entities = sessionEntities;
        }
        try {
          await this.$store.dispatch('content/endSession', payload);
          await this.$store.dispatch('meta/unloadDocument');
          await this.$store.dispatch('content/setLiveMode', true); // MLD-6883
        } catch (e) {
        }
      }
    },
    openNotesWindow() { // MLD-6435
      const route = {
        name: `${this.$root.routePrefix}.popup.presentation_note.edit`,
        params: {
          presentationID: this.presentation.id,
        },
        query: {
          page: this.pageNumber ?? 1,
        },
      };
      try {
        const url = this.$router.resolve(route).href;
        const windowID = 'NotesWindow';
        const windowHeight = 705; // Set your desired height here
        const windowWidth = screen.availWidth; // Get the full available width of the screen
        const windowTop = 0; // Align it to the top
        const windowLeft = 0; // Align it to the left
        const windowFeatures = `width=${windowWidth},height=${windowHeight},top=${windowTop},left=${windowLeft},toolbar=no,scrollbars=yes,resizable=yes,menubar=no`;
        this.notesWindow = window.open(url, windowID, windowFeatures);
      } catch (e) {
      }
    },
    closeNotesWindow() { // MLD-6435
      if (this.notesWindow && !this.notesWindow.closed) {
        this.notesWindow.close();
        this.notesWindow = null;
      }
    },
    updateNotesWindow(pageNumber) { // MLD-6435
      if (isInteger(pageNumber) && this.notesWindow) {
        try {
          const message = {
            type: 'changePage',
            pageNumber: pageNumber,
          }
          this.notesWindow.postMessage(message, window.location.origin)
        } catch (e) {
        }
      }
    },
  }
}
</script>
